import React from 'react';
import { Box, Card, CardContent, Divider, Skeleton, Typography } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip } from 'recharts';
import { useTranslate } from 'react-admin';
import { formatMoneyShort, formatInt } from 'src/utils/format';
import { EmptyContent } from 'src/components/empty-content';

export type UserVolumeDistItem = {
  bucket: string;
  percent: number;
  volume: number;
  users: number;
};

type UserVolumeTooltipProps = {
  rechartsProps?: any;
  t: ReturnType<typeof useTranslate>;
};

function UserVolumeTooltip({ rechartsProps, t }: UserVolumeTooltipProps) {
  const active = rechartsProps?.active as boolean | undefined;
  const payload = rechartsProps?.payload as any[] | undefined;

  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload as UserVolumeDistItem | undefined;
  if (!row) return null;

  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.61)',
        color: '#fff',
        padding: '10px 12px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.6,
        minWidth: 180,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{row.bucket}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span>{t('module.dashboardV2.userVolume.tooltip.pctOfTotalVol', { _: '% of Total Vol' })}</span>
        <span>{row.percent}%</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
        <span>{t('module.dashboardV2.userVolume.tooltip.volume', { _: 'Volume' })}</span>
        <span>{formatMoneyShort(row.volume)}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
        <span>{t('module.dashboardV2.userVolume.tooltip.users', { _: 'Users' })}</span>
        <span>{formatInt(row.users)}</span>
      </div>
    </div>
  );
}

export function UserVolumeDistributionCard({
  title,
  data,
  loading,
  totalVolume,
}: {
  title: string;
  data: UserVolumeDistItem[];
  loading?: boolean;
  totalVolume: Number;
}) {
  const t = useTranslate();
  const hasData = !!totalVolume;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ height: 450 }}>
          {loading ? (
            <Skeleton variant="rounded" height={450} />
          ) : !hasData ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyContent title={t('common.noData', { _: 'No data' })} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 4, right: 16, bottom: 4, left: 64 }}
                barSize={34}
                barCategoryGap="10%"
              >
                <XAxis type="number" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="bucket"
                  width={70}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />

                <ReTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={(rechartsProps) => <UserVolumeTooltip rechartsProps={rechartsProps} t={t} />}
                />

                <Bar dataKey="percent" radius={[8, 8, 8, 8]} fill="#000" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}